import { number } from "echarts";
import mySplitTags from "./splitTags";
import { Tag } from "@/types/prompt";

interface BracketMapping {
  [key: string]: string;
}
const bracketsCN: BracketMapping[] = [
  { "（": "(", "）": ")" },
  { "【": "[", "】": "]" },
  { "《": "<", "》": ">" },
  { "「": "{", "」": "}" },
  { "『": "{", "』": "}" },
  { "〈": "<", "〉": ">" },
  { "﹝": "(", "﹞": ")" },
  { "﹛": "{", "﹜": "}" },
  { "﹙": "(", "﹚": ")" },
];
const bracketsEN: BracketMapping[] = [
  { "(": "(", ")": ")" },
  { "[": "[", "]": "]" },
  { "{": "{", "}": "}" },
  { "<": "<", ">": ">" },
];

export const loraRegex = /^\<lora:\s*([^\:]+)\s*(:)?\s*(\-?[0-9\.]+)?([^\>]+)?\>/;
export const lycoRegex = /^\<lyco:\s*([^\:]+)\s*(:)?\s*(\-?[0-9\.]+)?([^\>]+)?\>$/;
export const weightNumRegex = /(.*):(\-?[0-9\.]+)/;
export const weightNumRegexEN = /(.*):\s*(\-?[0-9\.]+)/;
export const weightNumRegexCN = /(.*)：\s*(\-?[0-9\.]+)/;
/**
 * 替换括号
 * @param text
 * @returns {*}
 */
const replaceBrackets = (text: string): string => {
  const length = text.length;
  if (length === 0) return text;
  const replaces: BracketMapping = {};
  bracketsCN.forEach((item) => {
    for (const key in item) {
      replaces[key] = item[key];
    }
  });

  const start = text[0];
  const end = text[length - 1];
  if (typeof replaces[start] !== "undefined") {
    text = replaces[start] + text.substring(1);
  }
  if (typeof replaces[end] !== "undefined") {
    text = text.substring(0, length - 1) + replaces[end];
  }
  return text;
};
/**
 * 是否有括号
 * @param text {string}
 * @returns {Array|boolean}
 */
export const hasBrackets = (text: string): string[]|boolean => {
  const length = text.length;
  if (length === 0) return false;
  const brackets: string[][] = [];
  bracketsEN.forEach((item) => {
    const temp = [];
    for (const key in item) {
      temp.push(key);
    }
    brackets.push(temp);
  });
  bracketsCN.forEach((item) => {
    const temp = [];
    for (const key in item) {
      temp.push(key);
    }
    brackets.push(temp);
  });

  let start = text[0];
  let end = text[length - 1];
  for (const bracket of brackets) {
    if (bracket[0] === start && bracket[1] === end) {
      return bracket;
    }
  }
  return false;
};
/**
 * 替换标签
 * @param text {string}
 * @returns {*|string}
 */
export const replaceTag = (text: string): string => {
  if (typeof text !== "string") return text;
  if (text === "") return text;
  text = replaceBrackets(text);
  if (weightNumRegexEN.test(text)) text = text.replace(weightNumRegexEN, "$1:$2");
  if (weightNumRegexCN.test(text)) text = text.replace(weightNumRegexCN, "$1:$2");
  return text;
};

/**
 * 拆分标签
 * @param tag {string}
 * @returns {{left: string, right: string, value: string}}
 */
export const splitTag = (tag: string): { left: string; right: string; value: string } => {
  let result = { left: "", value: "", right: "" };
  let match = tag.match(/^([\(\<\{\[]+)(.*)$/);
  if (!match) {
    // 没有匹配到左括号
    result.value = tag;
    return result;
  }
  result.left = match[1];
  tag = match[2];
  match = tag.match(/((\:[0-9\.]+)?[\)\>\}\]]+)$/);
  if (!match) {
    // 没有匹配到右括号
    result.value = tag;
    return result;
  }
  result.right = match[1];
  tag = tag.substring(0, tag.length - result.right.length);
  result.value = tag;
  return result;
};

/**
 * 分割标签
 * @param tags {string}
 * @param autoBreakBeforeWrap {boolean}
 * @param autoBreakAfterWrap {boolean}
 * @returns {string[]}
 */
export const splitTags = (tags: string, autoBreakBeforeWrap: boolean = false, autoBreakAfterWrap: boolean = false): string[] => {
  return mySplitTags(tags, autoBreakBeforeWrap, autoBreakAfterWrap);
};

/**
 * 是否可以翻译
 * @param text {string}
 * @returns {boolean}
 */
export const canTranslate = (text: string): boolean => {
  // 如果为空，不翻译
  if (text.trim() === "") return false;
  // 如果<>包裹，不翻译
  if (text[0] === "<" && text[text.length - 1] === ">") return false;
  // 如果是数字、标点符号，不翻译
  const regex = /^[0-9`~!@#$%^&*()_+\-=\[\]{}\\|;:'",.\/<>?]+$/;
  if (regex.test(text)) return false;
  // 如果是单个英文字母，不翻译
  if (/^[a-zA-Z]$/.test(text)) return false;
  return true;
};

/**
 * 判断是否是英文
 * @param text {string}
 * @returns {boolean}
 */
export const isEnglish = (text: string): boolean => {
  const length = text.length;
  if (text[0] === "<" && text[length - 1] === ">") {
    return true;
  }
  // 通过ascii码判断
  for (let i = 0; i < length; i++) {
    if (text.charCodeAt(i) > 127) {
      return false;
    }
  }
  return true;
};

const canOneTranslate = (languageCode: string): boolean => {
  const detections = ["zh_CN", "zh_HK", "zh_TW", "ar_SA", "ja_JP", "ko_KR", "ru_RU"];
  detections.push("am_ET", "hy_AM", "as_IN", "bn_BD", "ba_RU", "bg_BG", "prs_AF", "dv_MV", "el_GR", "gu_IN", "he_IL", "hi_IN", "iu_CA", "kn_IN", "kk_KZ", "km_KH", "ku_Arab_IQ", "ky_KG", "lo_LA", "mk_MK", "ml_IN", "mr_IN", "mn_Cyrl_MN", "mn_Mong_CN", "my_MM", "ne_NP", "or_IN", "ps_AF", "fa_IR", "pa_Guru_IN", "sr_Cyrl_RS", "ta_IN", "tt_Latn_RU", "te_IN", "th_TH", "bo_CN", "ti_ET", "uk_UA", "ur_PK", "ug_Arab_CN", "vi_VN");
  if (!detections.includes(languageCode)) return false; // 无法检测是否英文
  return true;
};

/**
 * 检测是否英文
 * @param text {string}
 * @param languageCode {string}
 * @returns {number} 0: 不是英文，1: 是英文，2: 未知
 */
export const isEnglishByLangCode = (text: string, languageCode: string): number => {
  if (!canOneTranslate(languageCode)) return -1; // 无法检测是否英文

  const length = text.length;
  // 通过ascii码判断
  for (let i = 0; i < length; i++) {
    if (text.charCodeAt(i) > 127) {
      // 不是英文
      return 0;
    }
  }
  return 1;
};

/**
 * 实体化html
 * @param str {string}
 * @returns {string}
 */
export const escapeHtml = (str: string): string => {
  return str.replace(/[&<>'"]/g, (tag) => {
    const chars: any = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "'": "&#39;",
      '"': "&quot;",
    };
    return chars[tag] || tag;
  });
};

/**
 * 反实体化html
 * @param str {string}
 * @returns {string}
 */
export const unescapeHtml = (str: string): string => {
  return str.replace(/&amp;|&lt;|&gt;|&#39;|&quot;/g, (tag) => {
    const chars: any = {
      "&amp;": "&",
      "&lt;": "<",
      "&gt;": ">",
      "&#39;": "'",
      "&quot;": '"',
    };
    return chars[tag] || tag;
  });
};

/**
 * 获取标签的权重数
 * @param tag {string}
 * @returns {number}
 */
export const getTagWeightNum = (tag: string): number => {
  const match = tag.match(weightNumRegex);
  let weightNum = match ? parseFloat(match[2]) : 1;
  return weightNum;
};

/**
 * 获取标签的加权数
 * @param tag {string}
 * @param useNovelAiWeightSymbol {boolean}
 * @returns {number}
 */
export const getTagIncWeight = (tag: string, useNovelAiWeightSymbol: boolean = false): number => {
  if (useNovelAiWeightSymbol) {
    return countLayers(tag, "{", "}");
  } else {
    return countLayers(tag, "(", ")");
  }
};

/**
 * 获取标签的减权数
 * @param tag {string}
 * @returns {number}
 */
export const getTagDecWeight = (tag: string): number => {
  return countLayers(tag, "[", "]");
};

/**
 * 计算字符串包裹的层数
 * @param str {string}
 * @param start {string}
 * @param end {string}
 * @returns {number}
 */
const countLayers = (str: string, start: string = "(", end: string = ")"): number => {
  let count = 0;
  if (str.length < 2) return count; // 长度小于2，不可能有会有包裹
  while (true) {
    // 取出第一个和最后一个字符
    let first = str[0];
    let last = str[str.length - 1];
    if (first === start && last === end) {
      // 如果第一个和最后一个字符是括号，且是对应的括号，那么层数加1，然后去掉第一个和最后一个字符
      count++;
      // 去掉第一个和最后一个字符
      str = str.slice(1, str.length - 1);
    } else {
      break;
    }
  }
  return count;
};

/**
 * 设置字符串的包裹
 * @param str {string}
 * @param num {number}
 * @param start {string}
 * @param end {string}
 * @param joinStr {string}
 * @returns {string}
 */
export const setLayers = (str: string, num: number = 0, start: string = "(", end: string = ")", joinStr: string = ""): string => {
  // 先去除所有的括号
  while (true) {
    let first = str[0];
    let last = str[str.length - 1];
    if (first === start && last === end) {
      // 如果第一个和最后一个字符是括号，且是对应的括号，那么层数加1，然后去掉第一个和最后一个字符
      // 去掉第一个和最后一个字符
      str = str.slice(1, str.length - 1);
    } else {
      break;
    }
  }
  // 如果层数为0，那么直接返回
  if (num === 0) return str;
  // 如果层数大于0，那么在字符串的前面加上num个start，后面加上num个end
  return start.repeat(num) + str + joinStr + end.repeat(num);
};